import { useState, useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api, type PreviewDecision } from '../api';
import { useAuth } from '../auth';

export function StrategyProductsPage() {
  const { id: idParam } = useParams();
  const id = Number(idParam);
  const qc = useQueryClient();
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';

  const strategy = useQuery({ queryKey: ['strategy', String(id)], queryFn: () => api.strategyGet(id) });
  const products = useQuery({ queryKey: ['products', 'all'], queryFn: () => api.products({ limit: 500 }) });
  const jobs = useQuery({ queryKey: ['strategy-jobs', id], queryFn: () => api.strategyJobs(id), refetchInterval: 10_000 });

  const attached = useMemo(() => new Map(strategy.data?.products.map((p) => [p.productId.toString(), p]) ?? []), [strategy.data]);

  const [search, setSearch] = useState('');
  const [editing, setEditing] = useState<Record<string, { selected: boolean; minPrice?: string; maxPrice?: string }>>({});
  const [preview, setPreview] = useState<PreviewDecision[] | null>(null);

  // Инициализация состояния редактирования
  if (products.data && Object.keys(editing).length === 0) {
    const init: typeof editing = {};
    for (const p of products.data.items) {
      const att = attached.get(p.productId);
      init[p.productId] = {
        selected: Boolean(att),
        minPrice: att?.minPrice ?? '',
        maxPrice: att?.maxPrice ?? '',
      };
    }
    setEditing(init);
  }

  const visible = useMemo(() => {
    if (!products.data) return [];
    if (!search) return products.data.items;
    const q = search.toLowerCase();
    return products.data.items.filter((p) => p.name.toLowerCase().includes(q) || p.offerId.toLowerCase().includes(q));
  }, [products.data, search]);

  const save = useMutation({
    mutationFn: () => {
      const selected = Object.entries(editing)
        .filter(([_, v]) => v.selected)
        .map(([pid, v]) => ({
          productId: pid,
          minPrice: v.minPrice ? Number(v.minPrice) : undefined,
          maxPrice: v.maxPrice ? Number(v.maxPrice) : undefined,
        }));
      return api.strategySetProducts(id, selected);
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['strategy', String(id)] }); },
  });

  const previewRun = useMutation({
    mutationFn: () => api.strategyPreview(id),
    onSuccess: (r) => setPreview(r.decisions),
  });

  const runReal = useMutation({
    mutationFn: () => api.strategyRun(id, false),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['strategy-jobs', id] }),
  });

  if (strategy.isLoading) return <div className="card">Загрузка стратегии…</div>;
  if (!strategy.data) return <div className="card">Стратегия не найдена</div>;

  const selectedCount = Object.values(editing).filter((v) => v.selected).length;

  return (
    <>
      <div className="page-header">
        <h2>{strategy.data.name}: товары и прогон</h2>
        <div style={{ display: 'flex', gap: 8 }}>
          <Link to={`/strategies/${id}`} className="btn">← К настройкам</Link>
        </div>
      </div>

      <div className="card" style={{ marginBottom: 16 }}>
        <h3 style={{ marginTop: 0 }}>Действия</h3>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <button className="btn" disabled={previewRun.isPending} onClick={() => previewRun.mutate()}>
            {previewRun.isPending ? 'Считаю…' : 'Превью (dry-run)'}
          </button>
          {isAdmin && (
            <button className="btn primary" disabled={runReal.isPending || !strategy.data.enabled} onClick={() => {
              if (confirm(`Запустить стратегию на ${strategy.data!.products.length} товаров?`)) runReal.mutate();
            }}>
              {runReal.isPending ? 'Запуск…' : '▶ Запустить сейчас'}
            </button>
          )}
          {!strategy.data.enabled && <span className="muted" style={{ fontSize: 12 }}>Стратегия выключена — реальный запуск недоступен</span>}
        </div>

        {preview && <PreviewSummary decisions={preview} />}
      </div>

      <div className="card" style={{ marginBottom: 16 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
          <h3 style={{ margin: 0 }}>Товары ({selectedCount} выбрано из {products.data?.total ?? 0})</h3>
          <div style={{ display: 'flex', gap: 8 }}>
            <input className="search" placeholder="Поиск" value={search} onChange={(e) => setSearch(e.target.value)} />
            {isAdmin && (
              <button className="btn primary" onClick={() => save.mutate()} disabled={save.isPending}>
                {save.isPending ? 'Сохранение…' : 'Сохранить выбор'}
              </button>
            )}
          </div>
        </div>

        <table className="products">
          <thead>
            <tr>
              <th style={{ width: 30 }}>✓</th>
              <th>Название</th>
              <th>Артикул</th>
              <th className="num">Цена</th>
              <th>Min ₽</th>
              <th>Max ₽</th>
            </tr>
          </thead>
          <tbody>
            {visible.map((p) => {
              const e = editing[p.productId];
              if (!e) return null;
              return (
                <tr key={p.productId}>
                  <td><input type="checkbox" checked={e.selected} onChange={(ev) => setEditing({ ...editing, [p.productId]: { ...e, selected: ev.target.checked } })} disabled={!isAdmin} /></td>
                  <td>{p.name}</td>
                  <td>{p.offerId}</td>
                  <td className="num">{p.price ? `${p.price} ₽` : '—'}</td>
                  <td><input type="number" min={0} disabled={!e.selected || !isAdmin} value={e.minPrice} onChange={(ev) => setEditing({ ...editing, [p.productId]: { ...e, minPrice: ev.target.value } })} className="input" style={{ width: 90 }} /></td>
                  <td><input type="number" min={0} disabled={!e.selected || !isAdmin} value={e.maxPrice} onChange={(ev) => setEditing({ ...editing, [p.productId]: { ...e, maxPrice: ev.target.value } })} className="input" style={{ width: 90 }} /></td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="card">
        <h3 style={{ marginTop: 0 }}>История запусков</h3>
        <table className="products">
          <thead>
            <tr><th>Старт</th><th>Финиш</th><th>Статус</th><th className="num">Проверено</th><th className="num">Обновлено</th><th className="num">Пропущено</th></tr>
          </thead>
          <tbody>
            {jobs.data?.items.length === 0 && <tr><td colSpan={6} className="muted" style={{ textAlign: 'center', padding: 16 }}>Запусков ещё не было.</td></tr>}
            {jobs.data?.items.map((j) => (
              <tr key={j.id}>
                <td className="muted">{j.startedAt ? new Date(j.startedAt).toLocaleString('ru-RU') : '—'}</td>
                <td className="muted">{j.finishedAt ? new Date(j.finishedAt).toLocaleString('ru-RU') : '—'}</td>
                <td>{j.status}</td>
                <td className="num">{j.productsTried}</td>
                <td className="num" style={{ color: 'var(--green)' }}>{j.productsUpdated}</td>
                <td className="num">{j.productsSkipped}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}

function PreviewSummary({ decisions }: { decisions: PreviewDecision[] }) {
  const grouped = decisions.reduce<Record<string, number>>((acc, d) => {
    acc[d.decision.kind] = (acc[d.decision.kind] ?? 0) + 1;
    return acc;
  }, {});
  const changes = decisions.filter((d) => d.decision.kind === 'change').slice(0, 20);

  return (
    <div style={{ marginTop: 16, padding: 12, background: '#f0f9ff', borderRadius: 6 }}>
      <strong>Превью результата:</strong>
      <div style={{ marginTop: 8, fontSize: 13 }}>
        {Object.entries(grouped).map(([k, n]) => (
          <span key={k} style={{ marginRight: 12 }}>{k}: <strong>{n}</strong></span>
        ))}
      </div>
      {changes.length > 0 && (
        <table className="products" style={{ marginTop: 12 }}>
          <thead><tr><th>Артикул</th><th className="num">Новая цена</th><th>Правило</th></tr></thead>
          <tbody>
            {changes.map((d) => (
              <tr key={d.productId}>
                <td>{d.offerId}</td>
                <td className="num">{d.decision.kind === 'change' ? `${d.decision.newPrice} ₽` : ''}</td>
                <td className="muted">{d.decision.reason}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {changes.length < decisions.filter((d) => d.decision.kind === 'change').length && (
        <div className="muted" style={{ fontSize: 12, marginTop: 8 }}>
          …и ещё {decisions.filter((d) => d.decision.kind === 'change').length - 20} изменений.
        </div>
      )}
    </div>
  );
}
